import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/nocode';
import Card from '../../components/ui/Card';
import SafeIcon from '../../common/SafeIcon';
import { getCampStatus } from '../../utils/campLogic';

const DAYS = [
  { day: 1, title: 'The Christian', subtitle: 'Mindset & Calling' },
  { day: 2, title: 'The Creator', subtitle: 'Writing' },
  { day: 3, title: 'The Caretaker', subtitle: 'Publishing' },
  { day: 4, title: 'The Connector', subtitle: 'Marketing' },
  { day: 5, title: 'Compass Summary', subtitle: 'Review & Export' },
];

export default function MessHall() {
  const { user } = useAuth();
  const [contents, setContents] = useState({});
  const [settings, setSettings] = useState({});
  const [activeDay, setActiveDay] = useState(1);
  const [loading, setLoading] = useState(true);
  const [devMode, setDevMode] = useState(false);
  const location = useLocation();

  const { isAAP, currentTrailDay, windowOpen } = getCampStatus(user, settings);
  const isLockedOut = !windowOpen;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resContent, resSettings] = await Promise.all([
          api.get('camp_content_gwacamp', { block: 'messhall' }),
          api.get('settings_gwacamp')
        ]);
        
        const settingsData = Array.isArray(resSettings) ? resSettings : resSettings.data || [];
        const sMap = {};
        settingsData.forEach(s => {
          if (s.setting_key) sMap[s.setting_key] = s.value;
        });
        setSettings(sMap);

        const data = Array.isArray(resContent) ? resContent : resContent.data || [];
        const map = {};
        data.filter(item => item.is_active == 1 || item.is_active === true).forEach(c => {
          map[c.day] = c;
        });
        setContents(map);

        // Handle active day selection from URL
        const params = new URLSearchParams(location.search);
        const urlDay = parseInt(params.get('day'));
        
        if (urlDay && urlDay >= 1 && urlDay <= 5) {
          setActiveDay(urlDay);
        } else {
          setActiveDay(1);
        }
      } catch (err) {
        console.error("Failed to load mess hall content", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [location.search]);

  if (loading) {
    return (
      <div className="p-8 text-center text-muted flex flex-col items-center justify-center min-h-[400px]">
        <SafeIcon name="Loader" className="animate-spin text-4xl text-amber mb-4" />
        <p>Heading to the Mess Hall...</p>
      </div>
    );
  }

  if (isLockedOut) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4 animate-fadeIn">
        <Card className="text-center py-16 px-8 border-2 border-forest-green shadow-xl bg-[#EAF1E6]">
          <div className="w-24 h-24 bg-forest-green rounded-full flex items-center justify-center mx-auto mb-6 shadow-md text-white">
            <SafeIcon name="Users" className="text-5xl" />
          </div>
          <h1 className="text-3xl font-bold text-dark-green mb-4 font-poppins">Mess Hall Access Expired</h1>
          <p className="text-lg text-forest-green font-medium mb-6 max-w-2xl mx-auto">
            {settings.replay_expiry_msg || "Free pass replay access has expired. Upgrade to All-Access to continue watching all camp sessions."}
          </p>
          <a href="/#/upgrade">
            <button className="bg-amber text-white font-bold px-8 py-3 rounded hover:bg-orange-600 transition-colors">
              Upgrade to All-Access
            </button>
          </a>
        </Card>
      </div>
    );
  }

  if (currentTrailDay === 0 && user?.early_access != 1 && user?.role !== 'admin') {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4 animate-fadeIn">
        <Card className="text-center py-16 px-8 border-2 border-tan shadow-sm">
          <div className="w-20 h-20 bg-amber/20 rounded-full flex items-center justify-center mx-auto mb-6 text-amber">
            <SafeIcon name="Clock" className="text-4xl" />
          </div>
          <h1 className="text-3xl font-bold text-dark-green mb-4 font-poppins">Camp Hasn't Started Yet</h1>
          <p className="text-lg text-muted mb-6 max-w-2xl mx-auto">
            Mess Hall sessions unlock at 12:30 PM EST each day of camp. Check back on Day 1!
          </p>
        </Card>
      </div>
    );
  }

  const content = contents[activeDay];
  const isUnlocked = devMode || user?.role === 'admin' || user?.early_access == 1 || currentTrailDay >= activeDay;

  return (
    <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-8 pb-20 animate-fadeIn">
      {/* Sidebar Navigation */}
      <div className="w-full lg:w-72 shrink-0 space-y-4 no-print">
        <div className="bg-[#EAF1E6] border-l-4 border-forest-green p-4 rounded-r-md mb-6">
          <h1 className="text-xl font-bold text-dark-green flex items-center gap-2">
            <SafeIcon name="Users" className="text-amber" /> Midday Mess Hall
          </h1>
          <p className="text-xs mt-1 text-forest-green font-medium">Daily Check-in</p>
        </div>

        {user?.role === 'admin' && (
          <label className="flex items-center gap-2 text-xs font-bold text-dark-green bg-amber/10 border border-amber/30 rounded-md px-3 py-2 cursor-pointer">
            <input type="checkbox" checked={devMode} onChange={e => setDevMode(e.target.checked)} />
            Admin: Unlock All Days
          </label>
        )}
        
        <nav className="space-y-2">
          {DAYS.map((d) => {
            const unlocked = devMode || user?.role === 'admin' || user?.early_access == 1 || currentTrailDay >= d.day;
            const active = activeDay === d.day;
            return (
              <button
                key={d.day}
                onClick={() => setActiveDay(d.day)}
                className={`w-full text-left p-4 rounded-md border transition-all flex items-center justify-between
                  ${active ? 'bg-forest-green border-forest-green text-white shadow-md' : 
                    'bg-white border-tan hover:border-amber text-dark-green'}`}
              >
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider mb-1 opacity-80">Day {d.day}</div>
                  <div className={`font-bold ${active ? 'text-white' : 'text-dark-green'}`}>{d.title}</div>
                  <div className={`text-xs ${active ? 'text-tan' : 'text-muted'}`}>{d.subtitle}</div>
                </div>
                <div>
                  {!unlocked ? (
                    <SafeIcon name="Lock" className="text-gray-400" />
                  ) : active ? (
                    <SafeIcon name="ChevronRight" />
                  ) : (
                    <SafeIcon name="CheckCircle" className="text-amber" />
                  )}
                </div>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-center bg-white border-b border-tan pb-4 mb-6 pt-2 no-print">
          <div>
            <h2 className="text-2xl font-bold text-dark-green">Day {activeDay}: {DAYS[activeDay-1].title}</h2>
            <p className="text-muted">{DAYS[activeDay-1].subtitle}</p>
          </div>
        </div>

        {!isAAP && (
          <div className="bg-[#fdfaf6] border border-tan rounded-lg p-5 flex items-start gap-4 mb-8">
            <SafeIcon name="Info" className="text-amber mt-1 shrink-0" />
            <div className="text-sm text-dark-green leading-relaxed">
              <span className="font-bold">Replay Access:</span> Free pass holders have access to all session replays until{' '}
              <strong>{settings.replay_expiry
                ? new Date(settings.replay_expiry).toLocaleString('en-US', {
                    weekday: 'long', month: 'long', day: 'numeric',
                    hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
                  })
                : 'the end of the replay window'}</strong>. Upgrade to All-Access for lifetime replays.
            </div>
          </div>
        )}

        {content && isUnlocked ? (
          <div className="space-y-8">
            <Card className="overflow-hidden border-2 border-forest-green shadow-md">
              <div className="p-6">
                <div className="mb-2 inline-flex items-center gap-2 text-xs font-bold text-forest-green uppercase tracking-widest bg-forest-green/10 px-3 py-1 rounded-full w-fit">
                  Day {activeDay}
                </div>
                <h2 className="text-2xl font-bold text-dark-green mb-6 font-poppins leading-tight">
                  {content.session_title || `Mess Hall Day ${activeDay}`}
                </h2>
                
                {/* Video Area */}
                <div className="aspect-video bg-black rounded-md overflow-hidden relative shadow-inner group mb-6">
                  {content.video_embed ? (
                    <div 
                      className="w-full h-full [&>iframe]:w-full [&>iframe]:h-full border-0 absolute inset-0" 
                      dangerouslySetInnerHTML={{ __html: content.video_embed }} 
                    />
                  ) : content.video_url ? (
                    <iframe 
                      src={content.video_url} 
                      className="w-full h-full border-0 absolute inset-0"
                      allow="autoplay; fullscreen; picture-in-picture" 
                      allowFullScreen
                    ></iframe>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-gray-500 flex-col gap-2">
                      <SafeIcon name="VideoOff" className="text-3xl" />
                      <span className="text-sm font-medium">Video currently unavailable</span>
                    </div>
                  )}
                </div>

                {/* Description text */}
                {content.session_description && (
                  <div className="text-gray-700 leading-relaxed mb-6 whitespace-pre-wrap">
                    {content.session_description}
                  </div>
                )}

                {content.slides_url && (
                  <div className="mt-6 pt-4 border-t border-tan">
                    <a href={content.slides_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-gwa-blue font-bold hover:text-blue-800 transition-colors">
                      <SafeIcon name="Download" /> Download Session Slides
                    </a>
                  </div>
                )}
              </div>
            </Card>
          </div>
        ) : (
          <Card className="text-center py-16 bg-gray-50 border-dashed border-2 animate-fadeIn relative overflow-hidden">
            <div className="w-20 h-20 bg-[#EDE0CC] rounded-full flex items-center justify-center mb-6 mx-auto mt-4">
              <SafeIcon name="Lock" className="w-9 h-9 text-[#2D5016]" />
            </div>
            <h3 className="text-2xl font-bold text-dark-green mb-2">Session Not Yet Open</h3>
            <p className="text-muted max-w-sm mx-auto leading-relaxed">
              This session unlocks at <strong>12:30 PM EST</strong> on Day {activeDay}.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}